import { Logger } from "./logger";
import { AUTOTRACK_DEBOUNCE_MS } from "./config";

let isRunning = false;
let lastTrackedUrl: string | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pageviewCallback: (() => void) | null = null;

let originalPushState: typeof history.pushState | null = null;
let originalReplaceState: typeof history.replaceState | null = null;

function onNavigation(): void {
  if (!isRunning || !pageviewCallback) return;

  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(() => {
    debounceTimer = null;

    const currentUrl = window.location.href;
    if (currentUrl === lastTrackedUrl) {
      Logger.log("AutoTracker: URL unchanged, skipping");
      return;
    }

    lastTrackedUrl = currentUrl;
    Logger.log(`AutoTracker: navigation detected → ${window.location.pathname}`);
    pageviewCallback!();
  }, AUTOTRACK_DEBOUNCE_MS);
}

function patchHistory(): void {
  if (typeof history === "undefined") return;
  if (originalPushState !== null) return;

  originalPushState = history.pushState;
  originalReplaceState = history.replaceState;

  history.pushState = function (...args: Parameters<typeof history.pushState>) {
    originalPushState!.apply(this, args);
    onNavigation();
  };

  history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
    originalReplaceState!.apply(this, args);
    onNavigation();
  };

  Logger.log("AutoTracker: history.pushState/replaceState patched");
}

function unpatchHistory(): void {
  if (typeof history === "undefined") return;

  if (originalPushState !== null) {
    history.pushState = originalPushState;
    originalPushState = null;
  }
  if (originalReplaceState !== null) {
    history.replaceState = originalReplaceState;
    originalReplaceState = null;
  }

  Logger.log("AutoTracker: history methods restored");
}

function onPopState(): void {
  onNavigation();
}

export const AutoTracker = {
  start(onPageview: () => void): void {
    if (typeof window === "undefined") {
      Logger.warn("AutoTracker: skipped — no window (SSR)");
      return;
    }

    if (isRunning) {
      Logger.warn("AutoTracker: already running — ignoring duplicate start()");
      return;
    }

    pageviewCallback = onPageview;
    isRunning = true;
    lastTrackedUrl = window.location.href;

    window.addEventListener("popstate", onPopState);
    patchHistory();

    Logger.log(`AutoTracker: started (debounce: ${AUTOTRACK_DEBOUNCE_MS}ms)`);
  },

  stop(): void {
    if (!isRunning) return;

    if (typeof window !== "undefined") {
      window.removeEventListener("popstate", onPopState);
    }

    unpatchHistory();

    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }

    isRunning = false;
    lastTrackedUrl = null;
    pageviewCallback = null;

    Logger.log("AutoTracker: stopped");
  },

  isActive(): boolean {
    return isRunning;
  },
};
